// ============================================
// COSMIC HINDU - Checkout Page (Razorpay)
// ============================================

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '@context/CartContext';
import { useDiscounts } from '@context/DiscountContext';
import { formatPrice } from '@utils/helpers';
import { SHIPPING } from '@config/constants';
import orderService from '@services/orderService';

const RAZORPAY_KEY = import.meta.env.VITE_RAZORPAY_KEY || 'rzp_live_XXXXXXXXXXXXXXXX';

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function CheckoutPage() {
  const { items, subtotal, shipping, total, clearCart } = useCart();
  const { applyToCart, validateCoupon } = useDiscounts();
  const navigate = useNavigate();

  const [processing, setProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [form, setForm] = useState({
    name: '', email: '', phone: '', address: '', city: '', pincode: '',
  });
  const [errors, setErrors] = useState({});

  const update = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  // Sitewide discount
  const { discountedTotal, savedAmount } = applyToCart(total, items);
  const couponSave = couponResult?.discount_amount || 0;
  const finalTotal = Math.max(0, discountedTotal - couponSave);

  const handleCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponError('');
    try {
      const res = await validateCoupon(couponCode.trim(), discountedTotal);
      if (res.success) {
        setCouponResult(res);
      } else {
        setCouponError(res.message || 'Invalid coupon code');
        setCouponResult(null);
      }
    } catch {
      setCouponError('Could not apply coupon. Try again.');
      setCouponResult(null);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())     e.name     = 'Full name is required';
    if (!form.email.trim())    e.email    = 'Email is required';
    if (!form.phone.trim())    e.phone    = 'Phone number is required';
    if (!form.address.trim())  e.address  = 'Delivery address is required';
    if (!form.city.trim())     e.city     = 'City is required';
    if (!form.pincode.trim())  e.pincode  = 'Pincode is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePay = async () => {
    if (!validate()) return;
    setProcessing(true);
    try {
      // 1. Create order record (pending payment)
      const orderRes = await orderService.create({
        customer_name:    form.name,
        customer_email:   form.email,
        customer_phone:   form.phone,
        customer_city:    form.city,
        customer_address: `${form.address}, ${form.city} ${form.pincode}`,
        items: items.map((i) => ({
          product_id: i.id,
          name:       i.name,
          qty:        i.qty,
          price:      i.price,
        })),
        subtotal:         subtotal,
        shipping:         shipping,
        total:            finalTotal,
        payment_method:   'razorpay',
        coupon_code:      couponResult?.coupon?.code || null,
        discount_applied: savedAmount + couponSave,
        payment_status:   'pending',
      });

      const orderId = orderRes?.order_id || orderRes?.id || null;

      // 2. Load Razorpay and open checkout
      const loaded = await loadRazorpay();
      if (!loaded) {
        alert('Payment gateway failed to load. Please check your internet connection.');
        setProcessing(false);
        return;
      }

      const options = {
        key:         RAZORPAY_KEY,
        amount:      Math.round(finalTotal * 100), // paise
        currency:    'INR',
        name:        'Cosmic Hindu',
        description: `Order #${orderId || 'New'} — ${items.length} item${items.length > 1 ? 's' : ''}`,
        image:       '/favicon.svg',
        order_id:    orderRes?.razorpay_order_id || undefined,
        prefill: {
          name:    form.name,
          email:   form.email,
          contact: form.phone,
        },
        notes: {
          address:  `${form.address}, ${form.city} ${form.pincode}`,
          order_id: orderId,
        },
        theme: { color: '#D4A843' },
        handler: function (response) {
          // Payment successful
          clearCart();
          navigate('/order-success', {
            state: {
              orderId,
              paymentId: response.razorpay_payment_id,
              name: form.name,
            }
          });
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        setProcessing(false);
        alert('Payment failed. Please try again.');
      });
      rzp.open();
    } catch (e) {
      console.error('Checkout error:', e);
      setProcessing(false);
      alert('Something went wrong. Please try again.\n' + (e.message || ''));
    }
  };

  if (items.length === 0) {
    return (
      <section className="py-20 text-center">
        <div className="text-5xl mb-4">🛒</div>
        <p className="text-white/50 mb-4">Your cart is empty.</p>
        <Link to="/shop" className="btn-primary no-underline">Browse Products</Link>
      </section>
    );
  }

  return (
    <section className="py-10">
      <div className="max-w-5xl mx-auto px-5">

        {/* Header */}
        <Link to="/cart" className="text-gold-500/70 text-sm no-underline hover:text-gold-500 mb-6 inline-flex items-center gap-1">
          ← Back to Cart
        </Link>
        <h1 className="text-3xl font-bold text-temple-cream mb-8 font-display">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* ─── Left: Delivery Form ─── */}
          <div className="lg:col-span-3">
            <div className="border border-gold-500/15 rounded-2xl bg-white/[0.025] p-7">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-full bg-gold-500/15 flex items-center justify-center text-lg">📦</div>
                <h2 className="text-lg font-semibold text-temple-cream">Delivery Details</h2>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Full Name */}
                <div className="col-span-2">
                  <label className="block text-[11px] font-semibold text-gold-500/80 tracking-wider uppercase mb-1.5">Full Name *</label>
                  <input
                    className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-temple-cream text-sm outline-none transition-all placeholder:text-white/25
                      ${errors.name ? 'border-red-500/60 focus:border-red-400' : 'border-gold-500/20 focus:border-gold-500/60'}`}
                    value={form.name}
                    onChange={update('name')}
                    placeholder="Your full name"
                  />
                  {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-[11px] font-semibold text-gold-500/80 tracking-wider uppercase mb-1.5">Email *</label>
                  <input
                    type="email"
                    className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-temple-cream text-sm outline-none transition-all placeholder:text-white/25
                      ${errors.email ? 'border-red-500/60' : 'border-gold-500/20 focus:border-gold-500/60'}`}
                    value={form.email}
                    onChange={update('email')}
                    placeholder="your@email.com"
                  />
                  {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-[11px] font-semibold text-gold-500/80 tracking-wider uppercase mb-1.5">Phone *</label>
                  <input
                    type="tel"
                    className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-temple-cream text-sm outline-none transition-all placeholder:text-white/25
                      ${errors.phone ? 'border-red-500/60' : 'border-gold-500/20 focus:border-gold-500/60'}`}
                    value={form.phone}
                    onChange={update('phone')}
                    placeholder="+91 98765 43210"
                  />
                  {errors.phone && <p className="text-xs text-red-400 mt-1">{errors.phone}</p>}
                </div>

                {/* Address */}
                <div className="col-span-2">
                  <label className="block text-[11px] font-semibold text-gold-500/80 tracking-wider uppercase mb-1.5">Delivery Address *</label>
                  <textarea
                    className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-temple-cream text-sm outline-none transition-all placeholder:text-white/25 h-20 resize-none
                      ${errors.address ? 'border-red-500/60' : 'border-gold-500/20 focus:border-gold-500/60'}`}
                    value={form.address}
                    onChange={update('address')}
                    placeholder="Flat / House No., Street, Area"
                  />
                  {errors.address && <p className="text-xs text-red-400 mt-1">{errors.address}</p>}
                </div>

                {/* City */}
                <div>
                  <label className="block text-[11px] font-semibold text-gold-500/80 tracking-wider uppercase mb-1.5">City *</label>
                  <input
                    className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-temple-cream text-sm outline-none transition-all placeholder:text-white/25
                      ${errors.city ? 'border-red-500/60' : 'border-gold-500/20 focus:border-gold-500/60'}`}
                    value={form.city}
                    onChange={update('city')}
                    placeholder="Chennai"
                  />
                  {errors.city && <p className="text-xs text-red-400 mt-1">{errors.city}</p>}
                </div>

                {/* Pincode */}
                <div>
                  <label className="block text-[11px] font-semibold text-gold-500/80 tracking-wider uppercase mb-1.5">Pincode *</label>
                  <input
                    className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-temple-cream text-sm outline-none transition-all placeholder:text-white/25
                      ${errors.pincode ? 'border-red-500/60' : 'border-gold-500/20 focus:border-gold-500/60'}`}
                    value={form.pincode}
                    onChange={update('pincode')}
                    placeholder="600001"
                    maxLength={6}
                  />
                  {errors.pincode && <p className="text-xs text-red-400 mt-1">{errors.pincode}</p>}
                </div>
              </div>
            </div>

            {/* Razorpay Info */}
            <div className="mt-4 border border-gold-500/10 rounded-2xl bg-white/[0.015] p-5 flex items-start gap-4">
              <div className="text-2xl shrink-0">🔒</div>
              <div>
                <div className="text-sm font-semibold text-temple-cream mb-1">Secure Payment via Razorpay</div>
                <div className="text-xs text-white/40 leading-relaxed">
                  Pay safely with UPI, Net Banking, Credit/Debit Card, Wallets and more.
                  Your payment details are never stored on our servers.
                </div>
              </div>
            </div>
          </div>

          {/* ─── Right: Order Summary ─── */}
          <div className="lg:col-span-2">
            <div className="border border-gold-500/15 rounded-2xl bg-white/[0.025] p-6 sticky top-8">
              <h2 className="text-base font-semibold text-temple-cream mb-5 uppercase tracking-widest" style={{ fontFamily: "'Cinzel', serif", fontSize: '0.78rem' }}>
                Order Summary
              </h2>

              {/* Item list */}
              <div className="space-y-3 mb-5 max-h-52 overflow-y-auto pr-1">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg overflow-hidden border border-gold-500/10 shrink-0">
                      {item.images?.[0] || item.img ? (
                        <img src={item.images?.[0] || item.img} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gold-500/10 flex items-center justify-center text-lg">🕉️</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-temple-cream font-medium leading-tight truncate">{item.name}</div>
                      <div className="text-xs text-white/40 mt-0.5">Qty: {item.qty}</div>
                    </div>
                    <div className="text-xs font-bold text-gold-500 whitespace-nowrap">
                      {formatPrice(item.price * item.qty)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Price breakdown */}
              <div className="border-t border-gold-500/10 pt-4 space-y-2.5 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Subtotal</span>
                  <span className="text-temple-cream">{formatPrice(subtotal)}</span>
                </div>
                {savedAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-400">Discount</span>
                    <span className="text-emerald-400">−{formatPrice(savedAmount)}</span>
                  </div>
                )}
                {couponSave > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-400">Coupon ({couponResult?.coupon?.code})</span>
                    <span className="text-emerald-400">−{formatPrice(couponSave)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Shipping</span>
                  <span className={shipping === 0 ? 'text-emerald-400 font-medium' : 'text-temple-cream'}>
                    {shipping === 0 ? '✓ Free' : formatPrice(shipping)}
                  </span>
                </div>
              </div>

              {/* Coupon code */}
              <div className="mb-5">
                <div className="flex gap-2">
                  <input
                    className="flex-1 bg-white/5 border border-gold-500/20 rounded-xl px-3 py-2.5 text-temple-cream text-xs outline-none focus:border-gold-500/60 placeholder:text-white/25 transition-all"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Coupon code"
                  />
                  <button
                    onClick={handleCoupon}
                    className="px-4 py-2 rounded-xl border border-gold-500/40 text-gold-500 text-xs font-semibold bg-transparent cursor-pointer hover:bg-gold-500/10 transition-all whitespace-nowrap"
                  >
                    Apply
                  </button>
                </div>
                {couponError  && <p className="text-xs text-red-400 mt-1.5">{couponError}</p>}
                {couponResult && <p className="text-xs text-emerald-400 mt-1.5">✓ Coupon applied! Saving {formatPrice(couponSave)}</p>}
              </div>

              {/* Total */}
              <div className="border-t border-gold-500/15 pt-4 mb-5">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-temple-cream">Total Payable</span>
                  <span className="text-2xl font-bold text-gold-500 font-display">{formatPrice(finalTotal)}</span>
                </div>
                {(savedAmount + couponSave) > 0 && (
                  <div className="text-xs text-emerald-400 text-right mt-1">
                    You save {formatPrice(savedAmount + couponSave)} 🎉
                  </div>
                )}
              </div>

              {/* Pay Button */}
              <button
                onClick={handlePay}
                disabled={processing}
                className="w-full py-4 rounded-xl font-bold text-sm cursor-pointer transition-all flex items-center justify-center gap-2"
                style={{
                  background: processing ? 'rgba(212,168,67,0.4)' : 'linear-gradient(135deg, #D4A843, #FFB800)',
                  color: '#0F0A05',
                  border: 'none',
                  opacity: processing ? 0.7 : 1,
                }}
              >
                {processing ? (
                  <>
                    <span className="animate-spin">🌀</span> Processing…
                  </>
                ) : (
                  <>
                    🔐 Pay {formatPrice(finalTotal)} with Razorpay
                  </>
                )}
              </button>

              <div className="flex items-center justify-center gap-3 mt-4">
                {['UPI', 'Cards', 'NetBanking', 'Wallets'].map((m) => (
                  <span key={m} className="text-[10px] text-white/30 font-sans">{m}</span>
                ))}
              </div>
              <p className="text-[10px] text-white/25 text-center mt-2">
                All products are blessed before shipping 🙏
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
